import alpacaFinger from '@/assets/img/alpaca_finger.png'
import catImg from '@/assets/img/cat_test_img.png'
import { Button } from 'components/atoms/Button/Button';
import { ProgressBar } from 'components/atoms/ProgressBar/ProgressBar';
import clsx from 'clsx';
import { Icon } from '@/icons';
import  TrophyBadgeCard  from 'components/organisms/Card/TrohyBadgeCard';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { studentDashboardApi } from '@/services/api';
import { getSlugPrefix } from '@/utils/slug';

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const slugPrefix = getSlugPrefix(location.pathname);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);

  const renderEmptyState = useCallback(
    (message: string, onClick: () => void) => (
      <div className='flex flex-col gap-4 justify-center items-center p-10 text-center text-gray-500'>
        <p className='text-base font-medium text-gray-600'>{message}</p>
        <Button size='small' intent='secondary' type='button' onClick={onClick}>
          학습하러 가기
        </Button>
      </div>
    ),
    []
  );

  const buildStudentPath = useCallback(
    (path: string) => {
      const normalized = path.startsWith('/') ? path : `/${path}`;
      return slugPrefix ? `${slugPrefix}${normalized}` : normalized;
    },
    [slugPrefix]
  );

  const clampProgress = (value?: number | null) => {
    if (typeof value !== 'number') {
      return 0;
    }
    return Math.max(0, Math.min(100, value));
  };

  useEffect(() => {
    checkLoginAndLoadData();
  }, []);

  const checkLoginAndLoadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('student_token');
      
      if (!token) {
        setIsLoggedIn(false);
        setLoading(false);
        return;
      }

      setIsLoggedIn(true);

      // 대시보드 데이터 조회
      const res = await studentDashboardApi.getDashboardInfo();
      const data = res?.data || res;
      console.log('대시보드 데이터:', data);
      setDashboardData(data);
    } catch (error) {
      console.error('대시보드 데이터 조회 실패:', error);
      setIsLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLearningClick = useCallback(() => {
    // [QA Fix] 학습 CTA는 항상 학습 리스트로 이동
    navigate(buildStudentPath('/learning/list'));
  }, [buildStudentPath, navigate]);

  const handleHomeworkClick = useCallback(() => {
    navigate(buildStudentPath('/homework/list'));
  }, [buildStudentPath, navigate]);

  const handleWordbookClick = useCallback(() => {
    navigate(buildStudentPath('/vocabulary/list'));
  }, [buildStudentPath, navigate]);

  const handleBadgeMoreClick = useCallback(() => {
    navigate(buildStudentPath('/mypage/trophy_badge'));
  }, [buildStudentPath, navigate]);

  // 같은 trophyBadgeId를 기준으로 그룹화 (achievements.tsx와 동일 로직)
  const groupTrophyBadges = (items: any[]) => {
    const grouped = new Map();
    
    items.forEach(item => {
      const existing = grouped.get(item.trophyBadgeId);
      
      if (existing) {
        // 이미 존재하면 카운트 증가 및 최신 날짜 업데이트
        existing.count++;
        if (new Date(item.awardedAt) > new Date(existing.latestDate)) {
          existing.latestDate = item.awardedAt;
        }
      } else {
        // 새로 추가
        grouped.set(item.trophyBadgeId, {
          trophyBadgeId: item.trophyBadgeId,
          name: item.name,
          type: item.type,
          iconUrl: item.iconUrl,
          latestDate: item.awardedAt,
          count: 1
        });
      }
    });
    
    // 최신 날짜 기준으로 정렬하고 최대 5개만
    return Array.from(grouped.values())
      .sort((a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime())
      .slice(0, 5);
  };

  const groupedRecentBadges = dashboardData?.recentBadges ? groupTrophyBadges(dashboardData.recentBadges) : [];

  return (
    <main>
        <div className=''>
            {/* 상단 섹션 */}
            <section className=''>
                <div className='max-w-[1280px] w-full flex gap-5 px-5 items-start mx-auto overflow-hidden lg:pt-15 lg:pb-8 pt-6 pb-5'>
                    {/* 알파카 이미지 */}
                    <div className='relative max-w-[240px] mx-auto w-full h-full hidden lg:block'>
                        <img src={alpacaFinger} alt=""  className='absolute top-0 left-0'/>
                    </div>
                    <section className='flex flex-col gap-5 w-full lg:w-[calc(100%-240px)] lg:flex-row lg:h-[249px]'>                  
                        {/* 최근 학습 카드 */}
                        <div className='flex overflow-hidden flex-col w-full bg-white card !shadow-[2px_2px_10px_0px_rgba(0,0,0,0.08)] !rounded-2xl'>
                            <h1 className='px-5 pt-5 text-xl font-bold'>최근 학습</h1>
                            {loading ? (
                                <div className='flex justify-center items-center p-10 text-gray-500'>
                                    로딩 중...
                                </div>
                            ) : !isLoggedIn ? (
                                <div className='flex justify-center items-center p-10 text-gray-500'>
                                    로그인 해주세요
                                </div>
                            ) : dashboardData?.recentLearning ? (
                                <div className='flex gap-4 p-4 bg-white border-b border-gray-100 lg:border-none last:border-b-0'>
                                    <img 
                                        src={dashboardData?.recentLearning?.thumbnailUrl || catImg} 
                                        alt="" 
                                        className='aspect-[10/14] object-cover max-w-[120px] w-full rounded-lg border border-gray-200' 
                                    />
                                    <div className='flex flex-col flex-1 justify-between'>
                                        <div>
                                            <p className='text-2xl font-bold'>{dashboardData.recentLearning.scheduleName}</p>
                                            <div className='py-3'>
                                                <ProgressBar 
                                                    progress={clampProgress(dashboardData.recentLearning.progress)}
                                                    showTitle='학습 진도'
                                                />
                                            </div>
                                        </div>
                                        <Button type='button' onClick={handleLearningClick}>이어서 학습하기</Button>
                                    </div>
                                </div>
                            ) : (
                                <div className='flex flex-col flex-1 p-4 justify-center items-center  text-gray-500 min-h-[148px]'>
                                    <p className='p-10 text-xl font-bold'>학습 내역이 없습니다</p>
                                    <Button className='w-full' onClick={handleLearningClick}>학습 하러가기</Button>
                                </div>
                            )}
                        </div>
                        
                        {/* 최근 숙제 카드 */}  
                        <div className='flex overflow-hidden flex-col w-full bg-white card !shadow-[2px_2px_10px_0px_rgba(0,0,0,0.08)] !rounded-2xl min-w-[240px]'>
                            <h1 className='px-5 pt-5 text-xl font-bold'>최근 숙제</h1>
                            {loading ? (
                                <div className='flex flex-1 justify-center items-center p-10 text-gray-500'>
                                    로딩 중...
                                </div>
                            ) : !isLoggedIn ? (
                                <div className='flex flex-1 justify-center items-center p-10 text-gray-500'>
                                    로그인 해주세요
                                </div>
                            ) : dashboardData?.recentHomework ? (
                                <div className='flex gap-4 p-4 bg-white border-b border-gray-100 lg:border-none last:border-b-0'>
                                    <img 
                                        src={dashboardData?.recentHomework?.thumbnailUrl || catImg} 
                                        alt="" 
                                        className='aspect-[10/14] object-cover max-w-[120px] w-full rounded-lg border border-gray-200' 
                                    />
                                    <div className='flex flex-col flex-1 justify-between'>
                                        <div>
                                            <p className='text-2xl font-bold'>{dashboardData.recentHomework.scheduleName}</p>
                                            <div className='py-3'>
                                                <ProgressBar 
                                                    progress={clampProgress(dashboardData.recentHomework.progress)}
                                                    showTitle='숙제 진도'
                                                />
                                            </div>
                                        </div>
                                        <Button type='button' onClick={handleHomeworkClick}>이어서 숙제하기</Button>
                                    </div>
                                </div>
                            ) : (
                                // flex-1
                                <div className='flex flex-col flex-1 p-4 justify-center items-center  text-gray-500 min-h-[148px]'>
                                <p className='p-10 text-xl font-bold'>숙제 내역이 없습니다</p>
                                <Button className='w-full' onClick={handleHomeworkClick}>숙제 하러가기</Button>
                            </div>
                            )}
                        </div>
                    </section>
                </div>
            </section>

            <section className='flex flex-col gap-10 py-5 w-full bg-white rounded-t-2xl lg:py-10'>
                <div className='max-w-[1280px] w-full mx-auto px-5'>
                    <div className='flex justify-between items-center'>
                        <h2 className='text-xl font-bold'>오늘의 단어</h2>   
                        <Button text size='small' intent='secondary' className='flex items-center' onClick={handleWordbookClick}>
                            계속 학습하기<Icon name='arrowDown' size={20} className='*:fill-gray-500 rotate-270' />
                        </Button> 
                    </div>
                    <div className='flex flex-col flex-1 justify-between h-[148px] *:h-full'>
                        {loading ? (
                            <div className='flex justify-center items-center p-10 mt-3 text-gray-500 rounded-2xl back_gra_ravendar'>
                                로딩 중...
                            </div>
                        ) : !isLoggedIn ? (
                            <div className='flex justify-center items-center p-10 mt-3 text-gray-500 rounded-2xl back_gra_ravendar'>
                                로그인 해주세요
                            </div>
                        ) : dashboardData?.todayWord ? (
                            <div className='flex gap-5 justify-center items-center p-7 mt-3 text-5xl text-center rounded-2xl back_gra_ravendar'>
                                <p className='font-bold'>{dashboardData.todayWord.word}</p>
                                <p className='text-4xl font-bold text-indigo-500'>{dashboardData.todayWord.meaning}</p>
                            </div>
                        ) : (
                            <div className='flex justify-center items-center p-10 mt-3 text-gray-500 rounded-2xl back_gra_ravendar'>
                                단어장이 없습니다
                            </div>
                        )}
                    </div>
                </div>

                <div className='max-w-[1280px] w-full mx-auto px-5 '>
                    <div className='flex justify-between items-center'>
                        <h2 className='text-xl font-bold'>나의 트로피 및 뱃지</h2>    
                        <Button text size='small' intent='secondary' className='flex items-center' onClick={handleBadgeMoreClick}>
                            더보기<Icon name='arrowDown' size={20} className='*:fill-gray-500  rotate-270' />
                        </Button>
                    </div>
                    <div className='flex flex-col flex-1 justify-between'>
                        {loading ? (
                            <div className='flex justify-center items-center py-10 mt-3 text-gray-500 rounded-2xl card'>
                                로딩 중...
                            </div>
                        ) : !isLoggedIn ? (
                            <div className='flex justify-center items-center py-10 mt-3 text-gray-500 rounded-2xl card'>
                                로그인 해주세요
                            </div>
                        ) : groupedRecentBadges.length > 0 ? (
                            <div className='grid grid-cols-2 gap-5 justify-center items-center py-6 mt-3 text-center rounded-2xl lg:grid-cols-5 card lg:p-7'>
                                {groupedRecentBadges.map((badge: any) => (
                                    <TrophyBadgeCard 
                                        key={badge.trophyBadgeId}
                                        type={badge.type}
                                        name={badge.name}
                                        iconUrl={badge.iconUrl}
                                        date={badge.latestDate}
                                        count={badge.count}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className='grid grid-cols-2 gap-5 justify-center items-center py-6 mt-3 text-center rounded-2xl lg:grid-cols-5 card lg:p-7'>
                                <p className='col-span-2 py-10 text-gray-500 lg:col-span-5'>획득한 뱃지가 없습니다.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    </main>
  );
};

export default Home;